import { maskitoPhoneOptionsGenerator } from "@maskito/phone";
import { useMaskito } from "@maskito/react";
import type { CountryCode } from "libphonenumber-js";
import metadata from "libphonenumber-js/metadata.min.json";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useFormContext } from "react-hook-form";

import {
  Combobox,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
} from "@/shared/components/primitives/combobox";
import {
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useFormField,
} from "@/shared/components/primitives/form";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@/shared/components/primitives/input-group";
import {
  countryMatchesQuery,
  phoneCountryOptionFor,
  phoneCountryOptions,
  type PhoneCountryOption,
} from "@/shared/data/phone-country-list";
import useTranslations from "@/shared/lang/useTranslations";

// --- Public types ---

export type ColocaPhoneFieldProps = {
  /** Field name matching the Zod schema key. Must be registered in the parent `useForm`. */
  name: string;
  /** Visible label rendered above the field. */
  label?: ReactNode;
  /** Helper text rendered below. */
  description?: ReactNode;
  /** Placeholder for the phone input. Defaults to `"Phone number"`. */
  placeholder?: string;
  disabled?: boolean;
  /**
   * ISO 3166-1 alpha-2 codes to display, **in the order given**.
   *
   * Omit it. The default is `PHONE_COUNTRY_ISO2`, the curated phone allowlist, ordered LatAm-first and sorted
   * inside each region for the active language — which is what every surface in the app wants. The prop
   * survives for a field that genuinely needs a different set, and passing the residence allowlist through it
   * is the bug this list was introduced to fix: a Colombian resident can hold a US number.
   */
  countries?: readonly string[];
  /**
   * ISO 3166-1 alpha-2 code (e.g. `"CO"`) used as the initially selected country. Typically derived from the
   * authenticated user's profile so phone fields prefill with their dial code.
   *
   * A code outside the displayed list is **prepended to it** rather than ignored: a record stored before the
   * list changed must stay visible and editable, and an unresolved country leaves the input disabled — not
   * merely blank — so dropping it would lock the field.
   */
  defaultCountry?: string;
  /**
   * When set, the field will mirror the selected country ISO2 into the form field with this name on every
   * country change. Useful for cross-field validation (e.g. validate the phone against the selected
   * country).
   */
  countryFieldName?: string;
  className?: string;
};

// --- Flag image (SVG from flagcdn, works on every OS unlike emoji flags) ---

function FlagIcon({ iso2, size = 18 }: { iso2: string | null; size?: number }) {
  if (!iso2) {
    return (
      <span
        aria-hidden="true"
        className="inline-grid place-items-center rounded-[2px] bg-muted text-[10px] text-muted-foreground"
        style={{ width: size + 6, height: size }}
      >
        🌐
      </span>
    );
  }
  const code = iso2.toLowerCase();
  // flagcdn serves crisp SVGs by country code; 24px raster is a fallback.
  return (
    <img
      src={`https://flagcdn.com/${code}.svg`}
      width={size + 6}
      height={size}
      alt=""
      aria-hidden="true"
      loading="lazy"
      className="inline-block rounded-[2px] object-cover"
      style={{ width: size + 6, height: size }}
    />
  );
}

// --- Mask helpers ---

const PASSTHROUGH_MASK = { mask: /^[\s\S]*$/ };

function buildMaskOptions(iso2: string | null) {
  if (!iso2) return PASSTHROUGH_MASK;
  return maskitoPhoneOptionsGenerator({
    countryIsoCode: iso2 as CountryCode,
    // oxlint-disable-next-line @typescript-eslint/no-explicit-any
    metadata: metadata as any,
  });
}

// --- Country filter ---
// items are PhoneCountryOption objects at runtime; Combobox<string> types them as string.
function countryFilter(item: string, query: string): boolean {
  return countryMatchesQuery(item as unknown as PhoneCountryOption, query);
}

/** `+1 268` where the dial code is shared, plain `+1` / `+57` otherwise. */
function dialLabel(country: PhoneCountryOption): string {
  return country.areaCode ? `${country.dialCode} ${country.areaCode}` : country.dialCode;
}

// --- Inner component (useFormField valid here: called inside FormField render) ---

type PhoneFieldInnerProps = {
  value: string;
  onChange: (val: string) => void;
  onBlur: () => void;
  displayedCountries: readonly PhoneCountryOption[];
  disabled?: boolean;
  placeholder: string;
  defaultCountry?: string;
  onCountryChange?: (iso2: string | null) => void;
};

function PhoneFieldInner({
  value,
  onChange,
  onBlur,
  displayedCountries,
  disabled,
  placeholder,
  defaultCountry,
  onCountryChange,
}: PhoneFieldInnerProps) {
  const { formItemId, formDescriptionId, formMessageId, error } = useFormField();
  const { t } = useTranslations("global");
  const anchorRef = useRef<HTMLDivElement>(null);
  const initialCountry = useMemo(() => {
    if (!defaultCountry) return null;
    const upper = defaultCountry.toUpperCase();
    return displayedCountries.find((c) => c.iso2 === upper) ?? null;
  }, [defaultCountry, displayedCountries]);
  // The person's own choice, and `null` for "has not chosen yet" — which is not the same as choosing `null`,
  // hence the wrapper. Until they do, the selected country is DERIVED from the seed rather than copied into
  // state at mount: a `defaultCountry` that resolves a tick late (a session hydrating after the form mounts,
  // an `initialPhone.iso2` still empty) was otherwise lost for good, and an unresolved country leaves the
  // input DISABLED rather than merely blank — so the field stayed locked, with no way out of it.
  const [picked, setPicked] = useState<{ iso2: string | null } | null>(null);
  const countryIso2 = picked ? picked.iso2 : (initialCountry?.iso2 ?? null);

  // What the seed still has to PUSH: the dial code into the form value, so the prefix is on screen without
  // opening the picker, and the mirrored country for a caller that asked for one. Keyed by the seeded
  // country, so it follows a seed that arrives or changes late, and never runs twice for the same one.
  const seededRef = useRef<PhoneCountryOption | null>(null);

  useEffect(() => {
    if (picked || !initialCountry || seededRef.current?.iso2 === initialCountry.iso2) return;
    const previous = seededRef.current;
    seededRef.current = initialCountry;
    // Nothing typed is ever discarded: the value is rewritten only while it is empty or still carries
    // exactly what an earlier seed put there.
    const current = value.trim();
    if (!current || current === previous?.dialCode) onChange(initialCountry.dialCode);
    onCountryChange?.(initialCountry.iso2);
  }, [picked, initialCountry, value, onChange, onCountryChange]);

  const selectedCountry = useMemo(
    () => displayedCountries.find((c) => c.iso2 === countryIso2) ?? null,
    [countryIso2, displayedCountries],
  );

  const maskOptions = useMemo(() => buildMaskOptions(countryIso2), [countryIso2]);
  const maskRef = useMaskito({ options: maskOptions });

  const inputRef = useRef<HTMLInputElement | null>(null);
  const combinedRef = useCallback(
    (el: HTMLInputElement | null) => {
      inputRef.current = el;
      maskRef(el);
    },
    [maskRef],
  );

  const handleCountryChange = useCallback(
    (iso2: string | null) => {
      const country = displayedCountries.find((c) => c.iso2 === iso2) ?? null;
      setPicked({ iso2 }); // from here on the seed is ignored: a late one must not undo this choice
      onChange(country?.dialCode ?? "");
      onCountryChange?.(iso2);
      requestAnimationFrame(() => inputRef.current?.focus());
    },
    [onChange, onCountryChange, displayedCountries],
  );

  return (
    <Combobox
      value={countryIso2}
      onValueChange={handleCountryChange}
      onOpenChange={(open) => {
        if (!open) onBlur();
      }}
      items={displayedCountries as unknown as string[]}
      filter={countryFilter}
      itemToStringLabel={(v: string) => displayedCountries.find((c) => c.iso2 === v)?.name ?? v}
    >
      <InputGroup ref={anchorRef}>
        <InputGroupAddon align="inline-start">
          <InputGroupButton variant="ghost" render={<ComboboxTrigger />} disabled={disabled}>
            <FlagIcon iso2={selectedCountry?.iso2 ?? null} />
          </InputGroupButton>
        </InputGroupAddon>
        <InputGroupInput
          id={formItemId}
          ref={combinedRef}
          aria-describedby={error ? `${formDescriptionId} ${formMessageId}` : formDescriptionId}
          aria-invalid={!!error || undefined}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          disabled={disabled || !countryIso2}
          placeholder={countryIso2 ? placeholder : t("phone_field_select_country_placeholder")}
          type="tel"
        />
      </InputGroup>
      <ComboboxContent anchor={anchorRef}>
        <ComboboxInput
          showTrigger={false}
          showClear={false}
          placeholder={t("phone_field_search_country_placeholder")}
        />
        <ComboboxList>
          <ComboboxCollection>
            {(item: string) => {
              const c = item as unknown as PhoneCountryOption;
              return (
                <ComboboxItem key={c.iso2} value={c.iso2}>
                  <FlagIcon iso2={c.iso2} />
                  <span>{c.name}</span>
                  <span className="ml-auto text-xs tabular-nums text-muted-foreground">{dialLabel(c)}</span>
                </ComboboxItem>
              );
            }}
          </ComboboxCollection>
        </ComboboxList>
        <ComboboxEmpty>{t("not_found_message")}</ComboboxEmpty>
      </ComboboxContent>
    </Combobox>
  );
}

// --- Public component ---

/**
 * Phone number field wired to the nearest React Hook Form context.
 *
 * Combines a searchable country dial-code combobox with a maskito-formatted phone input inside a single
 * InputGroup container. The form value is the full formatted string including dial prefix (e.g. '+52 55 1234
 * 5678').
 */
export function ColocaPhoneField({
  name,
  label,
  description,
  placeholder,
  disabled,
  countries,
  defaultCountry,
  countryFieldName,
  className,
}: ColocaPhoneFieldProps) {
  const { control, setValue } = useFormContext();
  const { t, activeLanguage } = useTranslations("global");
  const resolvedPlaceholder = placeholder ?? t("input_phone_number_placeholder");

  // The default list is already ordered and localized; an explicit `countries` prop is mapped IN ITS OWN
  // ORDER rather than used to filter a catalog, so a caller that curated a sequence keeps it. Filtering was
  // the old behaviour and silently re-sorted every list alphabetically by ISO code.
  const displayedCountries = useMemo(() => {
    const base = countries
      ? countries
          .map((iso2) => phoneCountryOptionFor(iso2, activeLanguage))
          .filter((option): option is PhoneCountryOption => option !== null)
      : phoneCountryOptions(activeLanguage);

    // A stored country outside the list stays selectable — see `defaultCountry` on the props.
    const stored = defaultCountry?.trim().toUpperCase();
    if (!stored || base.some((option) => option.iso2 === stored)) return base;
    const preserved = phoneCountryOptionFor(stored, activeLanguage);
    return preserved ? [preserved, ...base] : base;
  }, [countries, defaultCountry, activeLanguage]);

  const handleCountryChange = useCallback(
    (iso2: string | null) => {
      if (!countryFieldName) return;
      setValue(countryFieldName, iso2 ?? "", { shouldValidate: true, shouldDirty: true });
    },
    [countryFieldName, setValue],
  );

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={className}>
          {label && <FormLabel>{label}</FormLabel>}
          <PhoneFieldInner
            value={(field.value as string) ?? ""}
            onChange={(val) => field.onChange(val)}
            onBlur={field.onBlur}
            displayedCountries={displayedCountries}
            disabled={disabled}
            placeholder={resolvedPlaceholder}
            defaultCountry={defaultCountry}
            onCountryChange={handleCountryChange}
          />
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
